import { signIn, SignInAuthorizationParams, SignInOptions, useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { Container } from 'theme-ui';
import styles from '@features/loginPage/LoginPage.styles';
import ActionBlock from '@features/loginPage/components/ActionBlock/ActionBlock';
import Heading from '@components/Heading/Heading';
import { KEYCLOAK_REGISTRATION_URI } from '@config/index';
import { TLoginView, TRedirectObject } from '@features/loginPage/typescript';
import Breadcrumbs from '@components/Breadcrumbs/Breadcrumbs';
import { LOGIN_PAGE_IMG, SESSION_STORAGE_REDIRECT_KEY } from '@features/loginPage/const';
import Loading from '@components/Loading/Loading';
import getPublicEnv from '@utils/getPublicEnv';
import { buildPostRegistrationRedirectPath } from '@utils/ensurePanelTrueSearchParam';
import { setOpenUserPanelAfterAuth } from '@utils/openUserPanelAfterAuth';
type PropsTypes = {
  viewType: TLoginView;
  redirectPath: string | false;
  showPanel: boolean;
  setLoginGeneralOpened?: (value: boolean) => void;
};
const LoginGeneral = ({
  viewType,
  redirectPath,
  showPanel,
  setLoginGeneralOpened
}: PropsTypes) => {
  const {
    t
  } = useTranslation(['user', 'common']);
  const {
    status
  } = useSession();
  const router = useRouter();
  const {
    locale,
    defaultLocale
  } = router;

  /** Path-only (no hash/query); avoid /fr/fr when Next already puts locale in asPath */
  const pathOnly = router.asPath.split('#')[0].split('?')[0] || '/';
  const basicPath = locale && locale !== defaultLocale ? pathOnly === `/${locale}` || pathOnly.startsWith(`/${locale}/`) ? pathOnly : `/${locale}${pathOnly === '/' ? '' : pathOnly}` : pathOnly;
  const handleRegister = () => {
    const localePrefix = locale !== defaultLocale ? `/${locale}` : '';
    if (redirectPath) {
      // Pod signup → router.push: flaga panelu w sessionStorage, ścieżka bez prefiksu locale (unikamy /fr/fr).
      const redirectScope = showPanel ? buildPostRegistrationRedirectPath(redirectPath, locale, defaultLocale) : redirectPath;
      sessionStorage.setItem(SESSION_STORAGE_REDIRECT_KEY, redirectScope);
      if (showPanel) {
        setOpenUserPanelAfterAuth();
      }
    }
    const redirectObject: TRedirectObject = {
      pathname: KEYCLOAK_REGISTRATION_URI,
      query: {
        redirect_uri: `${getPublicEnv('NEXT_PUBLIC_HOST')}${localePrefix}/signup`,
        client_id: getPublicEnv('NEXT_PUBLIC_KEYCLOAK_CLIENT_ID'),
        scope: 'openid email profile',
        response_type: 'code'
      }
    };
    if (locale !== defaultLocale) {
      redirectObject.query.ui_locales = locale;
    }
    router.replace(redirectObject);
  };
  const handleLogin = () => {
    const redirectBase = redirectPath === false ? basicPath : redirectPath === '/' ? locale && locale !== defaultLocale ? `/${locale}` : '/' : redirectPath;
    if (showPanel) {
      setOpenUserPanelAfterAuth();
    }
    const options: SignInOptions = {
      callbackUrl: redirectBase
    };
    const authorizationParams: SignInAuthorizationParams | undefined = locale && locale !== defaultLocale ? {
      ui_locales: locale
    } : undefined;
    signIn('keycloak', options, authorizationParams);
  };
  useEffect(() => {
    if (setLoginGeneralOpened) setLoginGeneralOpened(status === 'unauthenticated');
  }, [status]);
  if (status === 'unauthenticated') {
    return <>
        {redirectPath && showPanel && <Container>
            <Breadcrumbs steps={[{
          id: 0,
          name: t('home'),
          href: '/'
        }, {
          id: 1,
          name: t('login'),
          href: ''
        }]} />
          </Container>}
        <div sx={styles.section}>
          {showPanel && <div sx={styles.heading}>
              <Heading h={4} headingType={1} mobile align="center">
                {t(viewType.headerTitle)}
              </Heading>
            </div>}
          <div sx={styles.blocksContainer}>
            <div sx={styles.authBlocks}>
              <div sx={styles.singleAuthBlocks(true)} className="typeGeneral">
                <ActionBlock heading={t('alreadyHaveAccount').replace('{{domain}}', getPublicEnv('NEXT_PUBLIC_HOST').replace('https://', ''))} description={t('loginHelloMessage')} buttonText={t('login')} buttonAction={handleLogin} buttonOutlined={false} cy="InitializeLoginButton" />
              </div>
            </div>
            <div sx={styles.newClientBlock}>
              <ActionBlock heading={t('newClient')} description={t('joinToday')} buttonText={t('register')} buttonAction={() => handleRegister()} buttonOutlined fullHeight image={LOGIN_PAGE_IMG} descriptionPoints={[t('registerAccountPoint1'), t('registerAccountPoint2')]} cy="CreateAccount" />
            </div>
          </div>
        </div>
      </>;
  }
  return <div sx={styles.loading}>
      <Loading />
    </div>;
};
export default LoginGeneral;